// ante-react-kit/src/components/v2/AntEntityPicker/index.js
import React, { useState, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import './index.scss';
import AntLocalization from '../../../utils/AntLocalization';
import AntPopup from '../../AntPopup';

// ─── Helpers ─────────────────────────────────────────────────────
function getInitials(name) {
    if (!name) return '?';
    var parts = name.trim().split(/\s+/);
    var letters = parts.slice(-2).map(function (w) { return w[0]; }).join('');
    return letters.toUpperCase() || '?';
}

function formatNumber(n) {
    if (n === null || n === undefined || isNaN(n)) return '0';
    try { return Number(n).toLocaleString('vi-VN'); } catch (e) { return String(n); }
}

function hasNameOrPhone(item) {
    var name = (item.entity_name || item.name || '').trim();
    var phone = (item.entity_contact_phone || item.phone || '').trim();
    return name.length > 0 || phone.length > 0;
}

// ─── Detail Panel ────────────────────────────────────────────────
function DetailPanel(props) {
    var detail = props.detail;
    var loading = props.loading;
    var onConfirm = props.onConfirm;

    if (!detail && !loading) {
        return (
            <div className="v2-entity-picker__detail v2-entity-picker__detail--empty">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="18" r="8" stroke="#cbd5e1" strokeWidth="2" />
                    <path d="M8 40c0-8 7-12 16-12s16 4 16 12" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>{AntLocalization.translateText({ vi: 'Chọn khách hàng để xem chi tiết', en: 'Select a customer to see details', fr: 'Choisissez un client pour voir le détail', es: 'Seleccione un cliente para ver detalles', id: 'Pilih pelanggan untuk melihat detail', ja: '顧客を選択すると詳細が表示されます', zh: '选择客户以查看详情', ko: '고객을 선택하면 상세가 표시됩니다', hi: 'विवरण देखने हेतु ग्राहक चुनें', ar: 'اختر عميلًا لعرض التفاصيل' })}</span>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="v2-entity-picker__detail v2-entity-picker__detail--loading">
                <div className="v2-entity-picker__spinner" />
            </div>
        );
    }

    var name = detail.entity_name || '';
    var phone = detail.entity_contact_phone || '';
    var email = detail.entity_contact_email || detail.entity_email || '';
    var address = detail.entity_address || '';
    var loyaltyLevel = detail.entity_loyalty_level || detail.loyalty_level || '';
    var entityType = detail.type || '';
    var balanceAmt = detail.balance_amt;
    var arDebt = detail.ar_credit_net_inc;
    var apDebt = detail.ap_credit_net_inc;
    var tags = detail.tags;
    var groupCode = detail.entity_group_code || detail.group_code;
    var groupName = detail.entity_group_name || detail.group_name;

    return (
        <div className="v2-entity-picker__detail">
            <div className="v2-entity-picker__detail-header">
                <div className="v2-entity-picker__detail-avatar">{getInitials(name)}</div>
                <div className="v2-entity-picker__detail-headline">
                    <div className="v2-entity-picker__detail-name">{name || AntLocalization.translateText({ vi: '(Chưa có tên)', en: '(No name)', fr: '(Sans nom)', es: '(Sin nombre)', id: '(Tanpa nama)', ja: '（名前なし）', zh: '（未命名）', ko: '(이름 없음)', hi: '(नाम नहीं)', ar: '(بلا اسم)' })}</div>
                    {detail.code ? <div className="v2-entity-picker__detail-code">#{detail.code}</div> : null}
                </div>
            </div>

            <div className="v2-entity-picker__detail-body">
                {/* Contact section */}
                <div className="v2-entity-picker__detail-section">
                    <div className="v2-entity-picker__detail-section-title">
                        {AntLocalization.translateText({ vi: 'Liên hệ', en: 'Contact', fr: 'Contact', es: 'Contacto', id: 'Kontak', ja: '連絡先', zh: '联系人', ko: '연락처', hi: 'संपर्क', ar: 'الاتصال' })}
                    </div>
                    <div className="v2-entity-picker__detail-row">
                        <span className="v2-entity-picker__detail-label">{AntLocalization.translateText({ vi: 'SĐT', en: 'Phone', fr: 'Tél.', es: 'Tel.', id: 'Telp', ja: '電話', zh: '电话', ko: '전화', hi: 'फ़ोन', ar: 'هاتف' })}</span>
                        <span className="v2-entity-picker__detail-value">{phone || '—'}</span>
                    </div>
                    <div className="v2-entity-picker__detail-row">
                        <span className="v2-entity-picker__detail-label">{AntLocalization.translateText({ vi: 'Email', en: 'Email', fr: 'E-mail', es: 'Correo', id: 'Email', ja: 'メール', zh: '电子邮箱', ko: '이메일', hi: 'ईमेल', ar: 'البريد' })}</span>
                        <span className="v2-entity-picker__detail-value">{email || '—'}</span>
                    </div>
                    <div className="v2-entity-picker__detail-row">
                        <span className="v2-entity-picker__detail-label">{AntLocalization.translateText({ vi: 'Địa chỉ', en: 'Address', fr: 'Adresse', es: 'Dirección', id: 'Alamat', ja: '住所', zh: '地址', ko: '주소', hi: 'पता', ar: 'العنوان' })}</span>
                        <span className="v2-entity-picker__detail-value">{address || '—'}</span>
                    </div>
                </div>

                {/* Classification section */}
                <div className="v2-entity-picker__detail-section">
                    <div className="v2-entity-picker__detail-section-title">
                        {AntLocalization.translateText({ vi: 'Phân loại', en: 'Classification', fr: 'Classification', es: 'Clasificación', id: 'Klasifikasi', ja: '分類', zh: '分类', ko: '분류', hi: 'वर्गीकरण', ar: 'التصنيف' })}
                    </div>
                    <div className="v2-entity-picker__detail-row">
                        <span className="v2-entity-picker__detail-label">{AntLocalization.translateText({ vi: 'Hạng', en: 'Tier', fr: 'Niveau', es: 'Nivel', id: 'Tingkat', ja: 'ランク', zh: '等级', ko: '등급', hi: 'श्रेणी', ar: 'الفئة' })}</span>
                        <span className="v2-entity-picker__detail-value">
                            {loyaltyLevel ? <span className="v2-entity-picker__detail-badge v2-entity-picker__detail-badge--tier">{loyaltyLevel}</span> : '—'}
                        </span>
                    </div>
                    <div className="v2-entity-picker__detail-row">
                        <span className="v2-entity-picker__detail-label">{AntLocalization.translateText({ vi: 'Nhóm', en: 'Group', fr: 'Équipe', es: 'Equipo', id: 'Tim', ja: 'チーム', zh: '团队', ko: '팀', hi: 'टीम', ar: 'الفريق' })}</span>
                        <span className="v2-entity-picker__detail-value">{groupName || groupCode || '—'}</span>
                    </div>
                    <div className="v2-entity-picker__detail-row">
                        <span className="v2-entity-picker__detail-label">{AntLocalization.translateText({ vi: 'Loại', en: 'Type', fr: 'Type', es: 'Tipo', id: 'Jenis', ja: '種類', zh: '类型', ko: '유형', hi: 'प्रकार', ar: 'النوع' })}</span>
                        <span className="v2-entity-picker__detail-value">{entityType || '—'}</span>
                    </div>
                    {Array.isArray(tags) && tags.length > 0 ? (
                        <div className="v2-entity-picker__detail-row">
                            <span className="v2-entity-picker__detail-label">{AntLocalization.translateText({ vi: 'Tags', en: 'Tags', fr: 'Tags', es: 'Tags', id: 'Tag', ja: 'タグ', zh: '标签', ko: '태그', hi: 'टैग', ar: 'وسوم' })}</span>
                            <span className="v2-entity-picker__detail-value v2-entity-picker__detail-tags">
                                {tags.map(function (t, i) { return <span key={i} className="v2-entity-picker__detail-tag">{t}</span>; })}
                            </span>
                        </div>
                    ) : null}
                </div>

                {/* Financial section */}
                <div className="v2-entity-picker__detail-section">
                    <div className="v2-entity-picker__detail-section-title">
                        {AntLocalization.translateText({ vi: 'Tài chính', en: 'Financial', fr: 'Finances', es: 'Finanzas', id: 'Keuangan', ja: '財務', zh: '财务', ko: '재무', hi: 'वित्त', ar: 'المالية' })}
                    </div>
                    <div className="v2-entity-picker__detail-stats">
                        <div className="v2-entity-picker__detail-stat">
                            <div className="v2-entity-picker__detail-stat-label">{AntLocalization.translateText({ vi: 'Số dư', en: 'Balance', fr: 'Solde', es: 'Saldo', id: 'Saldo', ja: '残高', zh: '余额', ko: '잔액', hi: 'शेष', ar: 'الرصيد' })}</div>
                            <div className={'v2-entity-picker__detail-stat-value' + (balanceAmt < 0 ? ' v2-entity-picker__detail-stat-value--neg' : '')}>
                                {formatNumber(balanceAmt || 0)}
                            </div>
                        </div>
                        <div className="v2-entity-picker__detail-stat">
                            <div className="v2-entity-picker__detail-stat-label">{AntLocalization.translateText({ vi: 'Công nợ phải thu', en: 'Receivables', fr: 'Créances', es: 'Por cobrar', id: 'Piutang', ja: '売掛金', zh: '应收', ko: '미수금', hi: 'प्राप्य', ar: 'الذمم المدينة' })}</div>
                            <div className={'v2-entity-picker__detail-stat-value' + (arDebt > 0 ? ' v2-entity-picker__detail-stat-value--warn' : '')}>
                                {formatNumber(arDebt || 0)}
                            </div>
                        </div>
                        <div className="v2-entity-picker__detail-stat">
                            <div className="v2-entity-picker__detail-stat-label">{AntLocalization.translateText({ vi: 'Công nợ phải trả', en: 'Payables', fr: 'Dettes', es: 'Por pagar', id: 'Utang', ja: '買掛金', zh: '应付', ko: '미지급금', hi: 'देय', ar: 'الذمم الدائنة' })}</div>
                            <div className={'v2-entity-picker__detail-stat-value' + (apDebt > 0 ? ' v2-entity-picker__detail-stat-value--warn' : '')}>
                                {formatNumber(apDebt || 0)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="v2-entity-picker__detail-footer">
                <button className="v2-entity-picker__confirm-btn" onClick={onConfirm}>
                    {AntLocalization.translateText({ vi: 'Chọn khách', en: 'Select Customer', fr: 'Choisir le client', es: 'Elegir cliente', id: 'Pilih pelanggan', ja: '顧客を選択', zh: '选择客户', ko: '고객 선택', hi: 'ग्राहक चुनें', ar: 'اختيار العميل' })}
                </button>
            </div>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────
var AntEntityPicker = forwardRef(function AntEntityPicker(props, ref) {
    var { API, session, onSelect } = props;
    var popupRef = useRef(null);
    var [activeTab, setActiveTab] = useState('search');
    var [searchText, setSearchText] = useState('');
    var [searchResults, setSearchResults] = useState([]);
    var [loading, setLoading] = useState(false);
    var [selectedItem, setSelectedItem] = useState(null);
    var [detail, setDetail] = useState(null);
    var [detailLoading, setDetailLoading] = useState(false);
    var [newEntity, setNewEntity] = useState({ name: '', phone: '', email: '' });
    var searchTimerRef = useRef(null);

    useImperativeHandle(ref, function () {
        return {
            show: function () {
                setActiveTab('search');
                setSearchText('');
                setSearchResults([]);
                setSelectedItem(null);
                setDetail(null);
                setNewEntity({ name: '', phone: '', email: '' });
                if (popupRef.current) popupRef.current.show();
            },
            hide: function () {
                if (popupRef.current) popupRef.current.close();
            }
        };
    });

    var doSearch = useCallback(function (text) {
        if (!text || text.length < 2 || !API) return;
        setLoading(true);
        var searchPromise = API.postSearch
            ? API.postSearch({ searchText: text, limit: 30 })
            : Promise.resolve({ data: [] });
        searchPromise.then(function (result) {
            var raw = result && result.data ? result.data : [];
            // Lọc: chỉ giữ KH có tên hoặc SĐT
            var filtered = raw.filter(hasNameOrPhone);
            setSearchResults(filtered);
            setLoading(false);
        }).catch(function () {
            setSearchResults([]);
            setLoading(false);
        });
    }, [API]);

    var onSearchInput = useCallback(function (e) {
        var text = e.target.value;
        setSearchText(text);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(function () { doSearch(text); }, 400);
    }, [doSearch]);

    var openDetail = useCallback(function (item) {
        setSelectedItem(item);
        setDetail(item); // hiển thị ngay với dữ liệu list
        if (!API || !item.code) return;
        setDetailLoading(true);
        var detailPromise = API.postWithData
            ? API.postWithData('detail', { code: item.code })
            : (API.postDetail ? API.postDetail({ code: item.code }) : Promise.resolve({ data: item }));
        detailPromise.then(function (result) {
            var d = result && result.data ? result.data : item;
            setDetail(d);
            setDetailLoading(false);
        }).catch(function () {
            setDetailLoading(false);
        });
    }, [API]);

    var confirmSelect = useCallback(function () {
        if (!detail) return;
        if (onSelect) onSelect(detail);
        if (popupRef.current) popupRef.current.close();
    }, [detail, onSelect]);

    var createEntity = useCallback(function () {
        if (!newEntity.name) return;
        if (onSelect) onSelect({ entity_name: newEntity.name, entity_contact_phone: newEntity.phone, entity_email: newEntity.email, isNew: true });
        if (popupRef.current) popupRef.current.close();
    }, [newEntity, onSelect]);

    var title = AntLocalization.translateText({ vi: 'Chọn khách hàng', en: 'Select Customer', fr: 'Choisir le client', es: 'Elegir cliente', id: 'Pilih pelanggan', ja: '顧客を選択', zh: '选择客户', ko: '고객 선택', hi: 'ग्राहक चुनें', ar: 'اختيار العميل' });

    return (
        <AntPopup ref={popupRef} title={title} width={960} height={580}>
            <div className="v2-entity-picker">
                {/* Tab bar */}
                <div className="v2-entity-picker__tabs">
                    <button
                        className={'v2-entity-picker__tab' + (activeTab === 'search' ? ' v2-entity-picker__tab--active' : '')}
                        onClick={function () { setActiveTab('search'); }}
                    >
                        {AntLocalization.translateText({ vi: 'Tìm khách hàng', en: 'Search Customer', fr: 'Chercher un client', es: 'Buscar cliente', id: 'Cari pelanggan', ja: '顧客を検索', zh: '搜索客户', ko: '고객 검색', hi: 'ग्राहक खोजें', ar: 'بحث عن عميل' })}
                    </button>
                    <button
                        className={'v2-entity-picker__create-cta' + (activeTab === 'create' ? ' v2-entity-picker__create-cta--active' : '')}
                        onClick={function () { setActiveTab('create'); }}
                    >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                        <span>{AntLocalization.translateText({ vi: 'Tạo mới khách hàng', en: 'Create New Customer', fr: 'Nouveau client', es: 'Nuevo cliente', id: 'Pelanggan baru', ja: '顧客を新規作成', zh: '新建客户', ko: '새 고객 생성', hi: 'नया ग्राहक', ar: 'عميل جديد' })}</span>
                    </button>
                </div>

                {/* Search tab */}
                {activeTab === 'search' && (
                    <div className="v2-entity-picker__search">
                        <div className="v2-entity-picker__split">
                            {/* Left: list */}
                            <div className="v2-entity-picker__list-pane">
                                <div className="v2-entity-picker__search-bar">
                                    <span className="v2-entity-picker__search-icon">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <circle cx="6.5" cy="6.5" r="4.5" stroke="#94a3b8" strokeWidth="1.5" />
                                            <path d="M10.5 10.5L14 14" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>
                                    </span>
                                    <input
                                        className="v2-entity-picker__input"
                                        type="text"
                                        value={searchText}
                                        onChange={onSearchInput}
                                        placeholder={AntLocalization.translateText({ vi: 'Nhập tên, SĐT, email...', en: 'Name, phone, email...', fr: 'Nom, tél., e-mail...', es: 'Nombre, tel., email...', id: 'Nama, telepon, email...', ja: '名前、電話、メール...', zh: '姓名、电话、邮箱...', ko: '이름, 전화, 이메일...', hi: 'नाम, फ़ोन, ईमेल...', ar: 'الاسم، الهاتف، البريد...' })}
                                        autoFocus
                                    />
                                </div>
                                <div className="v2-entity-picker__results">
                                    {loading && (
                                        <div className="v2-entity-picker__state">
                                            <div className="v2-entity-picker__spinner" />
                                            <span>{AntLocalization.translateText({ vi: 'Đang tìm kiếm...', en: 'Searching...', fr: 'Recherche...', es: 'Buscando...', id: 'Mencari...', ja: '検索中...', zh: '搜索中...', ko: '검색 중...', hi: 'खोज रहे हैं...', ar: 'جارٍ البحث...' })}</span>
                                        </div>
                                    )}
                                    {!loading && searchResults.length === 0 && searchText.length >= 2 && (
                                        <div className="v2-entity-picker__state v2-entity-picker__state--empty">
                                            <span>{AntLocalization.translateText({ vi: 'Không tìm thấy kết quả', en: 'No results found', fr: 'Aucun résultat', es: 'Sin resultados', id: 'Tidak ada hasil', ja: '結果が見つかりません', zh: '未找到结果', ko: '결과 없음', hi: 'कोई परिणाम नहीं', ar: 'لا توجد نتائج' })}</span>
                                            <button className="v2-entity-picker__switch-btn" onClick={function () { setActiveTab('create'); }}>
                                                {AntLocalization.translateText({ vi: 'Tạo khách hàng mới', en: 'Create new customer', fr: 'Créer un client', es: 'Crear cliente nuevo', id: 'Buat pelanggan baru', ja: '新規顧客を作成', zh: '创建新客户', ko: '새 고객 생성', hi: 'नया ग्राहक बनाएँ', ar: 'إنشاء عميل جديد' })}
                                            </button>
                                        </div>
                                    )}
                                    {!loading && searchText.length < 2 && (
                                        <div className="v2-entity-picker__state v2-entity-picker__state--hint">
                                            <span>{AntLocalization.translateText({ vi: 'Nhập ít nhất 2 ký tự để tìm kiếm', en: 'Type at least 2 characters to search', fr: 'Saisissez au moins 2 caractères', es: 'Escriba al menos 2 caracteres', id: 'Ketik minimal 2 karakter untuk mencari', ja: '2文字以上入力して検索', zh: '至少输入2个字符进行搜索', ko: '2자 이상 입력해 검색하세요', hi: 'खोजने हेतु कम से कम 2 अक्षर लिखें', ar: 'اكتب حرفين على الأقل للبحث' })}</span>
                                        </div>
                                    )}
                                    {searchResults.map(function (item, idx) {
                                        var name = item.entity_name || item.name || '';
                                        var phone = item.entity_contact_phone || item.phone || '';
                                        var email = item.entity_contact_email || item.entity_email || item.email || '';
                                        var loyalty = item.entity_loyalty_level || item.loyalty_level || '';
                                        var isActive = selectedItem && selectedItem.id === item.id;
                                        return (
                                            <div
                                                key={item.id || idx}
                                                className={'v2-entity-picker__card' + (isActive ? ' v2-entity-picker__card--active' : '')}
                                                onClick={function () { openDetail(item); }}
                                            >
                                                <div className="v2-entity-picker__card-avatar">{getInitials(name)}</div>
                                                <div className="v2-entity-picker__card-body">
                                                    <div className="v2-entity-picker__card-name">
                                                        {name || AntLocalization.translateText({ vi: '(Chưa có tên)', en: '(No name)', fr: '(Sans nom)', es: '(Sin nombre)', id: '(Tanpa nama)', ja: '（名前なし）', zh: '（未命名）', ko: '(이름 없음)', hi: '(नाम नहीं)', ar: '(بلا اسم)' })}
                                                        {loyalty ? <span className="v2-entity-picker__card-tier">{loyalty}</span> : null}
                                                    </div>
                                                    <div className="v2-entity-picker__card-info">
                                                        {phone && <span className="v2-entity-picker__card-chip">{phone}</span>}
                                                        {email && <span className="v2-entity-picker__card-chip">{email}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Right: detail panel */}
                            <DetailPanel detail={detail} loading={detailLoading && !detail} onConfirm={confirmSelect} />
                        </div>
                    </div>
                )}

                {/* Create tab */}
                {activeTab === 'create' && (
                    <div className="v2-entity-picker__create">
                        <div className="v2-entity-picker__create-hint">
                            {AntLocalization.translateText({ vi: 'Nhập thông tin khách hàng mới. Có thể bổ sung chi tiết sau khi tạo.', en: 'Enter new customer info. Additional details can be added later.', fr: 'Saisissez les infos du nouveau client. Détails à compléter ensuite.', es: 'Ingrese los datos del nuevo cliente. Puede añadir detalles después.', id: 'Masukkan info pelanggan baru. Detail dapat ditambahkan nanti.', ja: '新規顧客の情報を入力してください。詳細は作成後に追加できます。', zh: '输入新客户信息。创建后可补充详情。', ko: '새 고객 정보를 입력하세요. 상세는 생성 후 추가할 수 있습니다.', hi: 'नए ग्राहक की जानकारी भरें। विवरण बाद में जोड़ सकते हैं।', ar: 'أدخل بيانات العميل الجديد. يمكن إضافة التفاصيل لاحقًا.' })}
                        </div>
                        <div className="v2-entity-picker__fields">
                            <div className="v2-entity-picker__field v2-entity-picker__field--required">
                                <label>
                                    {AntLocalization.translateText({ vi: 'Tên khách hàng', en: 'Customer Name', fr: 'Nom du client', es: 'Nombre cliente', id: 'Nama pelanggan', ja: '顧客名', zh: '客户名称', ko: '고객명', hi: 'ग्राहक का नाम', ar: 'اسم العميل' })}
                                    <span className="v2-entity-picker__required-mark">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newEntity.name}
                                    onChange={function (e) { setNewEntity(function (v) { return Object.assign({}, v, { name: e.target.value }); }); }}
                                    placeholder={AntLocalization.translateText({ vi: 'VD: Nguyễn Văn A', en: 'e.g. John Doe', fr: 'ex. John Doe', es: 'ej. John Doe', id: 'mis. John Doe', ja: '例: John Doe', zh: '例如：John Doe', ko: '예: John Doe', hi: 'उदा. John Doe', ar: 'مثال: John Doe' })}
                                    autoFocus
                                />
                            </div>
                            <div className="v2-entity-picker__field">
                                <label>{AntLocalization.translateText({ vi: 'Số điện thoại', en: 'Phone Number', fr: 'Téléphone', es: 'Teléfono', id: 'No. telepon', ja: '電話番号', zh: '电话号码', ko: '전화번호', hi: 'फ़ोन नंबर', ar: 'رقم الهاتف' })}</label>
                                <input
                                    type="tel"
                                    value={newEntity.phone}
                                    onChange={function (e) { setNewEntity(function (v) { return Object.assign({}, v, { phone: e.target.value }); }); }}
                                    placeholder={AntLocalization.translateText({ vi: 'VD: 0912 345 678', en: 'e.g. 0912 345 678', fr: 'ex. 0912 345 678', es: 'ej. 0912 345 678', id: 'mis. 0912 345 678', ja: '例: 0912 345 678', zh: '例如：0912 345 678', ko: '예: 0912 345 678', hi: 'उदा. 0912 345 678', ar: 'مثال: 0912 345 678' })}
                                />
                            </div>
                            <div className="v2-entity-picker__field">
                                <label>{AntLocalization.translateText({ vi: 'Email', en: 'Email', fr: 'E-mail', es: 'Correo', id: 'Email', ja: 'メール', zh: '电子邮箱', ko: '이메일', hi: 'ईमेल', ar: 'البريد' })}</label>
                                <input
                                    type="email"
                                    value={newEntity.email}
                                    onChange={function (e) { setNewEntity(function (v) { return Object.assign({}, v, { email: e.target.value }); }); }}
                                    placeholder={AntLocalization.translateText({ vi: 'VD: name@example.com', en: 'e.g. name@example.com', fr: 'ex. name@example.com', es: 'ej. name@example.com', id: 'mis. name@example.com', ja: '例: name@example.com', zh: '例如：name@example.com', ko: '예: name@example.com', hi: 'उदा. name@example.com', ar: 'مثال: name@example.com' })}
                                />
                            </div>
                        </div>
                        <div className="v2-entity-picker__create-footer">
                            <button
                                className="v2-entity-picker__submit"
                                onClick={createEntity}
                                disabled={!newEntity.name.trim()}
                            >
                                {AntLocalization.translateText({ vi: 'Tạo & chọn khách', en: 'Create & Select', fr: 'Créer et choisir', es: 'Crear y elegir', id: 'Buat & pilih', ja: '作成して選択', zh: '创建并选择', ko: '생성 후 선택', hi: 'बनाएँ और चुनें', ar: 'إنشاء واختيار' })}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </AntPopup>
    );
});

export default AntEntityPicker;
